import type {
  GetVehicleByVinResponse,
  InventoryVehicle,
  WebsiteVisibility,
} from '@/services/inventory'

/**
 * Pure display and decision helpers for the compact Inventory table (ISE-15).
 * No Vue, no stores, no service calls.
 */

const EM_DASH = '—'

/** Trimmed, lower-cased visibility reason; empty string when none. */
export function normalizeReason(reason: unknown): string {
  return typeof reason === 'string' ? reason.trim().toLowerCase() : ''
}

function humanizeEnum(value: string): string {
  const spaced = value.trim().replace(/[_\s]+/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * Label for an inventory state / usage type value.
 *
 * Missing values short-circuit to an em dash before any lookup. Only options with a non-empty
 * string value are matched, because the installed contracts leave some option values undefined at
 * runtime and an undefined option must never match (or label) a missing value. Unknown values
 * are humanized (`READY_FOR_SALE` -> "Ready for sale").
 */
export function formatInventoryEnumLabel(
  value: string | null | undefined,
  options: ReadonlyArray<{ label: string; value?: string | null }>,
): string {
  if (value == null || String(value).trim() === '') return EM_DASH
  const match = options.find(
    (option) => typeof option.value === 'string' && option.value !== '' && option.value === value,
  )
  return match ? match.label : humanizeEnum(String(value))
}

/** Condition badge text: New / Used / CPO for known values, others as given, null when absent. */
export function formatConditionBadge(condition: string | null | undefined): string | null {
  if (condition == null) return null
  const trimmed = String(condition).trim()
  if (trimmed === '') return null
  const lower = trimmed.toLowerCase()
  if (lower === 'new') return 'New'
  if (lower === 'used') return 'Used'
  if (lower === 'cpo') return 'CPO'
  return trimmed
}

/** Source badge text: "Manual" when `(source ?? feedType) === 'manual'`, else "Feed". */
export function inventorySourceLabel(vehicle: {
  source?: string | null
  feedType?: string | null
}): 'Manual' | 'Feed' {
  return (vehicle.source ?? vehicle.feedType) === 'manual' ? 'Manual' : 'Feed'
}

const ENTRY_DATE_UNAVAILABLE = 'Inventory entry date not available'

/**
 * Days-in-inventory tooltip text: a single inventory-entry date (R10). A stock date reads
 * "Entered inventory"; the ISE added date fallback reads "First recorded in ISE". The date is
 * formatted in the dealer time zone when one is supplied, otherwise in the runtime time zone.
 */
export function describeInventoryEntryDate({
  startMs,
  fromStockDate,
  timeZone,
}: {
  startMs: number | undefined
  fromStockDate: boolean
  timeZone?: string
}): string {
  if (startMs === undefined) return ENTRY_DATE_UNAVAILABLE
  try {
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      ...(timeZone ? { timeZone } : {}),
    }).format(new Date(startMs))
    return `${fromStockDate ? 'Entered inventory' : 'First recorded in ISE'}: ${formatted}`
  } catch {
    return ENTRY_DATE_UNAVAILABLE
  }
}

type VisibilityFields = {
  websiteVisibility?: string | null
  websiteVisibilityMode?: string | null
  websiteVisibilityReason?: string | null
}

/**
 * System-hidden predicate (D7/D8). Same rule Vehicle Details applies after normalisation: mode
 * counts as AUTO unless it is exactly MANUAL (a missing mode is AUTO, as the backend treats it),
 * visibility must be exactly HIDDEN, and the trimmed lower-cased reason must be one of the
 * automatic system reasons.
 */
export function isAutomaticSystemHidden(vehicle: VisibilityFields): boolean {
  if (vehicle.websiteVisibilityMode === 'MANUAL') return false
  if (vehicle.websiteVisibility !== 'HIDDEN') return false
  const reason = normalizeReason(vehicle.websiteVisibilityReason)
  return reason === 'hidden_by_rules' || reason === 'srp_missing'
}

/** Confirmed snapshot captured when the Manual pin dialog opens (R4.7). */
export type ManualPinSnapshot = {
  visibility: 'VISIBLE' | 'HIDDEN'
  mode: 'AUTO'
  /** Normalised (trimmed, lower-cased); empty when none. */
  reason: string
  systemHidden: boolean
}

/** Row fields refreshed from the preflight read when the state changed. */
export type ManualPinRefreshedFields = Pick<
  InventoryVehicle,
  | 'websiteVisibility'
  | 'websiteVisibilityMode'
  | 'websiteVisibilityReason'
  | 'websiteVisibilityChangedAt'
  | 'websiteVisibilityExplanation'
  | 'inventoryState'
  | 'membershipStatus'
>

export type ManualPinAssessment =
  | { outcome: 'proceed'; visibility: 'VISIBLE' | 'HIDDEN' }
  | { outcome: 'changed'; refreshed: ManualPinRefreshedFields }
  | { outcome: 'unverifiable' }

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined

/**
 * Compares the preflight read (`fetchVehicleByVin`) with the confirmed snapshot (R4.7).
 *
 * Field sources follow the real response shape: visibility, membership status are top level;
 * mode, inventory state and (preferentially) the reason live inside `vehicle`.
 */
export function assessManualPinPreflight(
  resp: GetVehicleByVinResponse | null | undefined,
  expected: ManualPinSnapshot,
): ManualPinAssessment {
  const vehicle = resp?.vehicle
  if (!resp || vehicle == null || typeof vehicle !== 'object' || Array.isArray(vehicle)) {
    return { outcome: 'unverifiable' }
  }

  const visibility = resp.websiteVisibility
  if (visibility !== 'VISIBLE' && visibility !== 'HIDDEN') return { outcome: 'unverifiable' }

  const membershipStatus = asString(resp.membershipStatus)
  if (!membershipStatus) return { outcome: 'unverifiable' }

  const mode: 'AUTO' | 'MANUAL' = vehicle.websiteVisibilityMode === 'MANUAL' ? 'MANUAL' : 'AUTO'
  const rawReason = asString(vehicle.websiteVisibilityReason) ?? asString(resp.websiteVisibilityReason)
  const reason = normalizeReason(rawReason)
  const inventoryState = asString(vehicle.inventoryState)
  const eligible = membershipStatus === 'CURRENT' && inventoryState !== 'ARCHIVED'
  const systemHidden = isAutomaticSystemHidden({
    websiteVisibility: visibility,
    websiteVisibilityMode: mode,
    websiteVisibilityReason: reason,
  })

  const changed =
    visibility !== expected.visibility ||
    mode !== expected.mode ||
    !eligible ||
    systemHidden !== expected.systemHidden ||
    (visibility === 'HIDDEN' && expected.visibility === 'HIDDEN' && reason !== expected.reason)

  if (!changed) return { outcome: 'proceed', visibility }

  const explanation = vehicle.websiteVisibilityExplanation
  const refreshed: ManualPinRefreshedFields = {
    websiteVisibility: visibility as WebsiteVisibility,
    websiteVisibilityMode: mode,
    websiteVisibilityReason: rawReason,
    websiteVisibilityChangedAt:
      asString(vehicle.websiteVisibilityChangedAt) ?? asString(resp.websiteVisibilityChangedAt),
    websiteVisibilityExplanation:
      explanation != null && typeof explanation === 'object'
        ? (explanation as InventoryVehicle['websiteVisibilityExplanation'])
        : undefined,
    inventoryState: inventoryState as InventoryVehicle['inventoryState'],
    membershipStatus: membershipStatus as InventoryVehicle['membershipStatus'],
  }
  return { outcome: 'changed', refreshed }
}

/**
 * One-line hidden details shared by the visibility badge tooltip and the Show confirmation dialog
 * (ISE-15 R3): explanation (trailing period removed), full reason, days hidden, Needs review.
 */
export function describeHiddenDetails({
  explanation,
  reasonLabel,
  daysHidden,
  stale,
}: {
  explanation: string
  reasonLabel: string | null
  daysHidden: number | null
  stale: boolean
}): string {
  const parts: string[] = [explanation.replace(/\.$/, '')]
  if (reasonLabel) parts.push(`Reason: ${reasonLabel}`)
  if (daysHidden !== null) {
    parts.push(
      daysHidden === 0
        ? 'Hidden today'
        : daysHidden === 1
          ? 'Hidden for 1 day'
          : `Hidden for ${daysHidden} days`,
    )
  }
  if (stale) parts.push('Needs review')
  return parts.join(' · ')
}

// Short badge cause from the existing ISE-12 evidence assessment (the result of
// formatWebsiteVisibilityExplanation). Unsupported or unrecognised assessments return null,
// so the badge reads plain "Hidden" and never names a cause the evidence does not support (R16).
const ASSESSED_HIDDEN_CAUSES: Record<string, string> = {
  'Hidden by the system — Vehicle data is unavailable (SRP missing).': 'SRP missing',
  'Hidden by the system — Website indexing failed.': 'Indexing failed',
  'Hidden by Publish Rules.': 'Publish Rules',
  'Hidden automatically — Inventory state is Sold.': 'Sold',
  'Hidden automatically — Inventory state is Unknown.': 'Unknown state',
}

export function hiddenBadgeCause(explanation: string): string | null {
  const fixed = ASSESSED_HIDDEN_CAUSES[explanation]
  if (fixed) return fixed
  const manual = /^Hidden manually — (.+)\.$/.exec(explanation)
  return manual && manual[1] !== 'No reason recorded' ? manual[1] : null
}

/** Shared explanation copy for the Auto / Manual visibility modes (tooltips and header popover). */
export const VISIBILITY_MODE_HELP = {
  AUTO: {
    title: 'Automatic visibility',
    text: 'ISE manages website visibility using inventory state and applicable system rules. Switching to Auto may change whether the vehicle is shown or hidden.',
  },
  MANUAL: {
    title: 'Manual visibility',
    text: 'You control whether the vehicle is shown or hidden. Switching to Manual keeps the current visibility and requires confirmation.',
  },
} as const
